import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { LOGO_URL, scrollToSection } from "../lib/constants";
import { useI18n } from "../context/I18nContext";

const SLIDES = [
  { url: "https://images.pexels.com/photos/13203179/pexels-photo-13203179.jpeg", label: "Real Estate" },
  { url: "https://images.unsplash.com/photo-1597609490757-5fc92ead5cb1", label: "Yachting" },
  { url: "https://images.unsplash.com/photo-1635975229704-0a420e777a56", label: "Automotive" },
  { url: "https://images.pexels.com/photos/28776068/pexels-photo-28776068.jpeg", label: "Aviation" },
];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.6 + i * 0.18, duration: 1.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = ({ onPrivateAccess }) => {
  const { t } = useI18n();
  const [index, setIndex] = useState(0);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 160]);
  const overlayOpacity = useTransform(scrollY, [0, 600], [1, 0]);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="home" data-testid="hero" className="relative h-screen w-full overflow-hidden bg-navy grain">
      {/* Slideshow */}
      <motion.div style={{ y }} className="absolute inset-0">
        <AnimatePresence>
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 1.6, ease: "easeInOut" }, scale: { duration: 6, ease: "linear" } }}
            className="absolute inset-0"
          >
            <img src={SLIDES[index].url} alt={SLIDES[index].label} className="w-full h-full object-cover" />
          </motion.div>
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-b from-navy/85 via-navy/60 to-navy" />
        <div className="absolute inset-0 bg-navy/40" />
      </motion.div>

      {/* Content */}
      <motion.div style={{ opacity: overlayOpacity }} className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
        <motion.img
          src={LOGO_URL}
          alt="Royce International"
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
          className="h-40 md:h-52 w-auto object-contain mb-6 drop-shadow-2xl"
        />

        <h1 data-testid="hero-title" className="font-serif text-white text-5xl sm:text-6xl md:text-8xl leading-[0.95] tracking-tight">
          <span className="reveal-line">
            <motion.span custom={0} variants={lineVariants} initial="hidden" animate="visible" className="block">
              {t("hero.line1")}
            </motion.span>
          </span>
          <span className="reveal-line">
            <motion.span custom={1} variants={lineVariants} initial="hidden" animate="visible" className="block italic text-gold">
              {t("hero.line2")}
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="font-sans text-white/70 text-sm md:text-base max-w-xl mt-8 leading-relaxed"
        >
          {t("hero.subtitle")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.7, duration: 1 }}
          className="flex flex-col sm:flex-row gap-4 mt-12"
        >
          <button
            data-testid="hero-discover-btn"
            onClick={() => scrollToSection("collections")}
            className="bg-gold text-navy px-9 py-4 rounded-md font-sans text-[11px] tracking-wide-lux uppercase hover:bg-gold-light transition-colors duration-400"
          >
            {t("hero.discover")}
          </button>
          <button
            data-testid="hero-private-access-btn"
            onClick={onPrivateAccess}
            className="border border-white/40 text-white px-9 py-4 rounded-md font-sans text-[11px] tracking-wide-lux uppercase hover:border-gold hover:text-gold transition-all duration-400"
          >
            {t("hero.requestAccess")}
          </button>
        </motion.div>
      </motion.div>

      {/* Slide indicators */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex gap-3">
        {SLIDES.map((s, i) => (
          <button
            key={s.label}
            onClick={() => setIndex(i)}
            className={`h-[2px] transition-all duration-500 ${i === index ? "w-10 bg-gold" : "w-5 bg-white/30"}`}
            aria-label={s.label}
          />
        ))}
      </div>
    </section>
  );
};
